import { Router } from '@angular/router';
import { Component, HostListener, OnInit } from '@angular/core';
import { RouteGuardService } from './guard/route-guard.service';
import { UtentiService } from './services/utenti.service';

import { AuthService } from './services/auth.service';
import { bearerToken } from './app.module';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OpzioniService } from './services/opzioni.service';
import { QRCodeService } from './services/qrcode.service';
import { KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { SocketioService } from './services/socketio.service';
import dayjs from 'dayjs';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  isOnline = navigator.onLine;
  private refreshTimer: any; // Riferimento al timer per poterlo cancellare


  constructor(
    public guard: RouteGuardService,
    private router: Router,
    private uSvc: UtentiService,
    private authSvc: AuthService,
    private opzSvc: OpzioniService,
    private qrSvc: QRCodeService,
    private keycloak: KeycloakService,
    private socketioService: SocketioService,
  ) {}

  @HostListener('window:offline', ['$event'])
  onOffline(event: Event): void {
    this.isOnline = false;
    console.warn('Browser is offline. Pausing token refresh schedule.');
    // Se c'è un refresh pianificato, lo cancelliamo.
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
  }

  @HostListener('window:online', ['$event'])
  onOnline(event: Event): void {
    this.isOnline = true;
    console.log('Browser is back online. Resuming token refresh schedule.');
    // Ripianifichiamo immediatamente un controllo.
    this.scheduleTokenRefresh();
  }

  ngOnInit(): void {
    bearerToken.pipe(untilDestroyed(this)).subscribe(initialToken => {
      if (initialToken) {
        this.authSvc.setBearerToken(initialToken);
        console.log('Keycloak authentication successful. Initial token received.');
        this.scheduleTokenRefresh(); // Avvia il ciclo di refresh
      }
    });

    /* this.keycloak.keycloakEvents$.subscribe({
      next: (event) => {
        console.log('Keycloak event received:', event);
        if (event.type === KeycloakEventType.OnAuthSuccess) {
          console.log('Keycloak authentication successful.');
          const exp = this.keycloak.getKeycloakInstance().tokenParsed.exp;
          const refreshIn = new Date(exp * 1000).getTime() - Date.now() - 20000;
          setTimeout(() => {
            this.keycloak.updateToken(25)
            .then(refreshed => {
              if (refreshed) {
                this.keycloak.getToken().then(token => {
                  const data = dayjs().format('YYYY-MM-DD HH:mm:ss');
                  console.log(`Keycloak token refreshed successfully at ${data}:`, token);
                  bearerToken.next(token);
                });
              }
            })
            .catch(error => {
              console.error('Keycloak: Error refreshing token after expiry:', error);
              // Handle critical refresh failure: redirect to login
              this.keycloak.login(); // Force re-login
            });
          }, refreshIn);
        }
      },
    }); */

    

    const temaDefault = 'teom';
    this.caricaTema('tema-grafico-default', temaDefault);
    this.impostaIcona(temaDefault);
    /* this.opzSvc.getTemaWebapp().then(tema => {
      this.caricaTema('tema-grafico-custom', tema);
      // carico anche il tema di default, che sarà inserito *prima*
      // di quello custom, quindi meno prioritario.
      // In questo modo nel tema custom se voglio posso definire solo
      // alcune variabili e quelle mancanti saranno prese dal tema di
      // default
      this.caricaTema('tema-grafico-default', temaDefault);
      this.impostaIcona(tema);
    }).catch(err => {
      console.error(err);
      // in caso di problemi carico comunque il tema di default
      this.caricaTema('tema-grafico-default', temaDefault);
      this.impostaIcona(temaDefault);
    }); */
  }

  private scheduleTokenRefresh(): void {
    // Se c'era un timer precedente, lo cancelliamo per evitare sovrapposizioni
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
    if (!this.isOnline) return; // Non schedulare se siamo offline

    const keycloakInstance = this.keycloak.getKeycloakInstance();
    if (!keycloakInstance || !keycloakInstance.tokenParsed) {
      console.warn('Keycloak: Istanza o token non disponibili per la schedulazione del refresh.');
      return;
    }

    const exp = keycloakInstance.tokenParsed.exp;
    const timeSkewInMs = (keycloakInstance.timeSkew || 0) * 1000;

    // Calcoliamo il tempo rimanente usando l'orario del client corretto con il timeSkew
    const correctedClientNow = Date.now() - timeSkewInMs;

    // Schedula il refresh 20 secondi prima della scadenza
    const refreshInMs = (exp * 1000) - correctedClientNow - 20000;
    const effectiveRefreshInMs = Math.max(5000, refreshInMs);

    console.log(`Keycloak: Prossimo refresh schedulato tra ${effectiveRefreshInMs / 1000} secondi (clock skew di ${timeSkewInMs / 1000}s applicato).`);

    this.refreshTimer = setTimeout(async () => {
      try {
        const refreshed = await this.attemptTokenRefreshWithRetry();
        if (refreshed) {
          const newToken = await this.keycloak.getToken();
          const data = dayjs().format('YYYY-MM-DD HH:mm:ss');
          console.log(`Keycloak: Token aggiornato con successo alle ${data}.`);
          console.log(newToken);
          this.authSvc.setBearerToken(newToken);
          bearerToken.next(newToken); // Emettiamo dopo aver aggiornato il servizio
        } else {
          console.log('Keycloak: Il token non necessitava di refresh.');
        }
      } catch (error) {
        console.error('Keycloak: Fallimento critico del refresh del token dopo i tentativi. Forzo il login.', error);
        this.keycloak.login();
      } finally {
        // In ogni caso (successo o no-refresh), schedula il prossimo controllo
        this.scheduleTokenRefresh();
      }
    }, effectiveRefreshInMs);
  }

  /**
   * Tenta di aggiornare il token. Se la chiamata non risponde entro 2 secondi,
   * riprova ogni 2 secondi fino a ottenere una risposta.
   * @returns Promise<boolean> che si risolve con il risultato di updateToken.
   * @throws Un errore se il refresh fallisce in modo non recuperabile (es. 401).
   */
  private async attemptTokenRefreshWithRetry(): Promise<boolean> {
    if (!this.isOnline) return false; // Non tentare se siamo offline

    while (true) {
      console.log('Keycloak: Tentativo di aggiornamento token...');
      try {
        const refreshed = await this.keycloak.updateToken(25);
        console.log('Keycloak: Risposta da updateToken ricevuta.', { refreshed });
        return refreshed; // Successo, esce dal ciclo e restituisce il risultato.
      } catch (error) {
        // Se andiamo offline durante il tentativo, interrompiamo il loop di retry
        if (!this.isOnline) {
          console.warn('Connessione persa durante il tentativo di refresh. Il ciclo di refresh è in pausa.');
          return false; // Esce dalla funzione, verrà ripresa dall'evento 'online'
        }
        // Se l'errore indica che la sessione è scaduta, non ha senso riprovare.
        // Purtroppo, keycloak-js spesso lancia un errore generico.
        // La logica di fallback è forzare il login nel chiamante.
        // Qui ci limitiamo a riprovare in caso di errori di rete/timeout.
        console.warn(`Keycloak: Tentativo di refresh fallito. Riprovo tra 5 secondi... Errore:`, error);
        // Controlla se l'utente è ancora autenticato prima di riprovare
        if (!await this.keycloak.isLoggedIn()) throw new Error("Utente non più loggato.");
        await new Promise(resolve => setTimeout(resolve, 5000)); // Attende 5 secondi prima di riprovare.
      }
    }
  }

  async onLogout() {
    this.uSvc.onLogout(); // cancella il sessionId da sessionStorage
    await this.guard.logout();
    this.authSvc.setBearerToken(undefined);
    this.authSvc.setIdUtenteLoggato(undefined);
    this.router.navigateByUrl('/');
  }

  caricaTema(linkId: string, tema: string) {
    this.aggiungiOModificaLink(linkId, tema, 'stylesheet', 'text/css', 'style.css');
  }

  aggiungiOModificaLink(linkId: string, tema: string, rel: string, type: string, hrefFile: string) {
    let link = document.getElementById(linkId) as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = rel;
      link.type = type;

      const head = document.getElementsByTagName('head')[0];
      head.prepend(link);
    }

    link.href = `assets/temi/${tema}/${hrefFile}`;
  }

  impostaIcona(tema: string) {
    this.aggiungiOModificaLink('favicon-link', tema, 'icon', 'image/png', 'favicon.png');
  }


}
